import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import pb from '../../lib/pocketbase';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFilePdf, faEnvelope, faPhone, faClock, faCheck, faTimes,
    faUserPlus, faExternalLinkAlt, faSearch, faQuoteLeft, faUserCircle,
    faHardHat, faUserTie, faShieldAlt, faBriefcase, faExclamationTriangle, faUserShield
} from '@fortawesome/free-solid-svg-icons';
import styles from '../../pages/Dashboard.module.css';

const ApplicationManagement = () => {
    const { refreshData } = useOutletContext();
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedApp, setSelectedApp] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Hiring Modal States
    const [isHireModalOpen, setIsHireModalOpen] = useState(false);
    const [candidateToHire, setCandidateToHire] = useState(null);
    const [selectedRole, setSelectedRole] = useState('employee');
    const [hiringProcessing, setHiringProcessing] = useState(false);

    useEffect(() => {
        fetchApplications();
    }, []);

    const fetchApplications = async () => {
        try {
            setLoading(true);
            // Fetch users who are strictly categorized as candidates
            const records = await pb.collection('users').getFullList({
                filter: 'profile_type = "candidate"',
                sort: '-created',
            });
            setApplications(records);
        } catch (error) {
            console.error("Failed to fetch candidates from users collection:", error);
            toast.error("Erreur lors du chargement des candidatures");
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'NOUVEAU': return '#3B82F6'; // Blue
            case 'EN_COURS': return '#F59E0B'; // Orange
            case 'ENTRETIEN': return '#8B5CF6'; // Purple
            case 'ACCEPTE': return '#10B981'; // Green
            case 'REFUSE': return '#EF4444'; // Red
            default: return '#6B7280';
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await pb.collection('users').update(id, { application_status: newStatus });
            setApplications(prev => prev.map(app => app.id === id ? { ...app, application_status: newStatus } : app));
            toast.success(`Statut mis à jour : ${newStatus}`);
        } catch (error) {
            toast.error("Erreur lors de la mise à jour");
        }
    };

    const getRoleLabel = (role) => {
        const roles = {
            'admin': 'Admin',
            'employee': 'Bureau / Employé',
            'client': 'Client',
            'user': 'Candidat'
        };
        return roles[role] || (role === 'user' ? 'Candidat' : role);
    };

    const handleHireClick = (app) => {
        setCandidateToHire(app);
        setIsHireModalOpen(true);
        setSelectedRole('employee');
    };

    const finalizeHire = async () => {
        if (!candidateToHire) return;

        try {
            setHiringProcessing(true);
            const user = candidateToHire;

            // Determine profile type based on role
            let profileType = 'staff';
            if (selectedRole === 'client') profileType = 'client';
            if (selectedRole === 'user') profileType = 'candidate';

            // IMPORTANT: Removed 'verified: true' because it's a protected system field
            // that can cause 400 errors when updated with other fields via Client API.
            const updatePayload = {
                role: selectedRole,
                profile_type: profileType,
                application_status: 'ACCEPTE',
                emailVisibility: true // Ensure email is visible
            };

            console.log("DEBUG: Data to update:", updatePayload);
            const response = await pb.collection('users').update(user.id, updatePayload);
            console.log("DEBUG: PB Response:", response);

            if (response.role !== selectedRole) {
                console.warn("Role mismatch in response. Check API rules.");
                toast.warning(`Le statut est mis à jour, mais le rôle reste "${response.role}". Vérifiez les droits admin.`);
            } else {
                const typeLabel = profileType === 'client' ? 'Client' : 'Équipe';
                toast.success(`Succès ! ${user.name} fait maintenant partie des ${typeLabel} en tant que ${getRoleLabel(selectedRole)}.`);
            }

            // Update local state
            setApplications(prev => prev.map(item => item.id === user.id ? { ...item, application_status: 'ACCEPTE', role: response.role } : item));

            if (refreshData) refreshData();
            setIsHireModalOpen(false);

        } catch (error) {
            console.error("DEBUG: HIRING FAILED!", error);

            let errorMessage = "Échec de l'embauche";

            // Handle PocketBase specific error data
            if (error.data && typeof error.data === 'object') {
                const fieldErrors = Object.entries(error.data)
                    .map(([field, details]) => `${field}: ${details.message || JSON.stringify(details)}`)
                    .join(" | ");
                errorMessage += ` (${fieldErrors})`;
                console.error("Detailed Validation Errors:", error.data);
            } else {
                errorMessage += `: ${error.message || "Erreur inconnue"}`;
            }

            toast.error(errorMessage);
        } finally {
            setHiringProcessing(false);
        }
    };

    const getCvUrl = (app) => {
        if (!app.cv) return null;
        return pb.files.getURL(app, app.cv);
    };

    const filteredApplications = applications.filter(app =>
        (app.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (app.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className={styles.loading}>Chargement des candidatures...</div>;

    return (
        <div>
            <div className={styles.header}>
                <h2>Gestion du Recrutement</h2>
                <div className={styles.headerActions}>
                    <div className={styles.searchBar} style={{ position: 'relative' }}>
                        <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                        <input
                            type="text"
                            placeholder="Rechercher un candidat..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ paddingLeft: '35px', borderRadius: '20px', border: '1px solid #e2e8f0', height: '40px', width: '250px' }}
                        />
                    </div>
                </div>
            </div>

            <div className={styles.dataSection}>
                <div className={styles.tableContainer}>
                    <table className={styles.dataTable}>
                        <thead>
                            <tr>
                                <th>Candidat</th>
                                <th>Contact</th>
                                <th>Statut</th>
                                <th>CV</th>
                                <th>Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredApplications.length > 0 ? (
                                filteredApplications.map(app => (
                                    <tr key={app.id}>
                                        <td>
                                            <div style={{ fontWeight: '700', color: 'var(--color-primary)' }}>{app.name}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{getRoleLabel(app.role)}</div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <a href={`mailto:${app.email}`} style={{ textDecoration: 'none', color: 'var(--color-primary)', fontSize: '0.9rem', fontWeight: '600' }}>
                                                    <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '6px', opacity: 0.6 }} /> {app.email}
                                                </a>
                                                {app.phone && (
                                                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                                        <FontAwesomeIcon icon={faPhone} style={{ marginRight: '6px', opacity: 0.6 }} /> {app.phone}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <select
                                                value={app.application_status}
                                                onChange={(e) => handleStatusChange(app.id, e.target.value)}
                                                style={{
                                                    padding: '6px 28px 6px 14px',
                                                    borderRadius: '20px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '700',
                                                    backgroundColor: `${getStatusColor(app.application_status)}20`,
                                                    color: getStatusColor(app.application_status),
                                                    border: '1px solid transparent',
                                                    cursor: 'pointer',
                                                    outline: 'none',
                                                    appearance: 'none',
                                                    WebkitAppearance: 'none',
                                                    MozAppearance: 'none',
                                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='${encodeURIComponent(getStatusColor(app.application_status))}'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                                    backgroundRepeat: 'no-repeat',
                                                    backgroundPosition: 'right 10px center',
                                                    backgroundSize: '12px',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.filter = 'brightness(0.95)';
                                                    e.target.style.transform = 'translateY(-1px)';
                                                    e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.filter = 'none';
                                                    e.target.style.transform = 'none';
                                                    e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                                }}
                                            >
                                                <option value="NOUVEAU">Nouveau</option>
                                                <option value="EN_COURS">En cours</option>
                                                <option value="ENTRETIEN">Entretien</option>
                                                <option value="ACCEPTE">Accepté</option>
                                                <option value="REFUSE">Refusé</option>
                                            </select>
                                        </td>
                                        <td>
                                            {app.cv ? (
                                                <a
                                                    href={getCvUrl(app)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.viewBtn}
                                                    style={{ color: '#ef4444' }}
                                                    title="Voir le CV"
                                                >
                                                    <FontAwesomeIcon icon={faFilePdf} size="lg" />
                                                </a>
                                            ) : (
                                                <span style={{ color: '#cbd5e1' }}>Aucun CV</span>
                                            )}
                                        </td>
                                        <td>
                                            <div style={{ fontSize: '0.85rem' }}>
                                                <FontAwesomeIcon icon={faClock} style={{ marginRight: '5px', opacity: 0.5 }} />
                                                {new Date(app.created).toLocaleDateString('fr-FR')}
                                            </div>
                                        </td>
                                        <td className={styles.actions}>
                                            <button
                                                onClick={() => handleHireClick(app)}
                                                title="Embaucher (Mettre à jour rôle)"
                                                style={{ color: '#10b981' }}
                                                disabled={app.application_status === 'ACCEPTE'}
                                            >
                                                <FontAwesomeIcon icon={faUserPlus} />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (app.application_message) {
                                                        setSelectedApp(app);
                                                        setIsModalOpen(true);
                                                    } else {
                                                        toast.info("Aucun message de motivation.");
                                                    }
                                                }}
                                                title="Voir le message"
                                            >
                                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                                        Aucune candidature trouvée.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MESSAGE MODAL */}
            {isModalOpen && selectedApp && (
                <div className={styles.modalOverlay} onClick={() => setIsModalOpen(false)}>
                    <div className={styles.messageModal} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <div className={styles.modalTitle}>
                                <FontAwesomeIcon icon={faEnvelope} />
                                <h3>Lettre de Motivation</h3>
                            </div>
                            <button className={styles.closeModalBtn} onClick={() => setIsModalOpen(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.candidateInfoSummary}>
                                <div className={styles.candidateMeta}>
                                    <span>Candidat</span>
                                    <strong>{selectedApp.name}</strong>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <span style={{ display: 'block', fontSize: '0.8rem', color: '#64748b' }}>Reçu le</span>
                                    <strong style={{ fontSize: '0.9rem' }}>{new Date(selectedApp.created).toLocaleDateString('fr-FR')}</strong>
                                </div>
                            </div>
                            <div className={styles.messageQuote}>
                                <FontAwesomeIcon icon={faQuoteLeft} className={styles.quoteIcon} />
                                {selectedApp.application_message}
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                className={styles.addButton}
                                onClick={() => setIsModalOpen(false)}
                                style={{ padding: '8px 25px' }}
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* HIRE MODAL */}
            {isHireModalOpen && candidateToHire && (
                <div className={styles.modalOverlay} onClick={() => setIsHireModalOpen(false)}>
                    <div className={styles.messageModal} style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <div className={styles.modalTitle}>
                                <FontAwesomeIcon icon={faUserPlus} />
                                <h3>Recrutement de {candidateToHire.name}</h3>
                            </div>
                            <button className={styles.closeModalBtn} onClick={() => setIsHireModalOpen(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <p style={{ margin: '0 0 var(--spacing-lg)', color: 'var(--color-text-light)', fontSize: '0.9rem' }}>
                                Choisissez le poste que vous souhaitez attribuer à ce nouveau membre de l'équipe :
                            </p>

                            <div className={styles.roleSelectionGrid}>
                                <div
                                    className={`${styles.roleOption} ${selectedRole === 'employee' ? styles.active : ''}`}
                                    onClick={() => setSelectedRole('employee')}
                                >
                                    <div className={styles.roleIconCircle}><FontAwesomeIcon icon={faHardHat} /></div>
                                    <h4>Employé</h4>
                                    <span>Terrain ou Bureau</span>
                                </div>
                                <div
                                    className={`${styles.roleOption} ${selectedRole === 'admin' ? styles.active : ''}`}
                                    onClick={() => setSelectedRole('admin')}
                                >
                                    <div className={styles.roleIconCircle}><FontAwesomeIcon icon={faUserShield} /></div>
                                    <h4>Administrateur</h4>
                                    <span>Gestion totale</span>
                                </div>
                                <div
                                    className={`${styles.roleOption} ${selectedRole === 'client' ? styles.active : ''}`}
                                    onClick={() => setSelectedRole('client')}
                                >
                                    <div className={styles.roleIconCircle}><FontAwesomeIcon icon={faBriefcase} /></div>
                                    <h4>Client</h4>
                                    <span>Accès Partenaire</span>
                                </div>
                                <div
                                    className={`${styles.roleOption} ${selectedRole === 'user' ? styles.active : ''}`}
                                    onClick={() => setSelectedRole('user')}
                                >
                                    <div className={styles.roleIconCircle}><FontAwesomeIcon icon={faUserCircle} /></div>
                                    <h4>Candidat</h4>
                                    <span>Rôle par défaut</span>
                                </div>
                            </div>

                            <div className={styles.hireConfirmBox}>
                                <FontAwesomeIcon icon={faCheck} style={{ marginRight: '10px', color: '#10b981' }} />
                                Le profil de <strong>{candidateToHire.email}</strong> sera mis à jour avec le rôle <strong>{selectedRole}</strong>.
                            </div>
                        </div>
                        <div className={styles.modalFooter} style={{ gap: '12px' }}>
                            <button className={styles.viewBtn} onClick={() => setIsHireModalOpen(false)}>
                                Annuler
                            </button>
                            <button
                                className={styles.addButton}
                                onClick={finalizeHire}
                                disabled={hiringProcessing}
                            >
                                {hiringProcessing ? 'Traitement...' : 'Confirmer l\'embauche'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApplicationManagement;
